"use client";

import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { ProductsService } from "@/services/ProductsService";
import type { ProductsOutput } from "@/interfaces/products";
import type { TagsOutput, PaginatedResponse, Pagination } from "@/interfaces";
import { format } from "date-fns";
import {
  Trash,
  Plus,
  Filter,
  X,
  LayoutGrid,
  List,
  Edit,
  ImageIcon,
  Tag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Substituir formatações monetárias por numberToMoney
import { numberToMoney } from "@/util/moneyConversion";

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  const { page, pageSize, total, pages } = pagination;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2">
      <div className="text-sm text-gray-500">
        Mostrando {total > 0 ? startItem : 0} a {endItem} de {total} produtos
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center mr-4">
          <span className="text-sm text-gray-500 mr-2">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {/* Mostrar pelo menos a página 1 mesmo quando há apenas uma página */}
            {pages === 0 ? (
              <Button
                variant="default"
                size="icon"
                className="h-8 w-8 bg-[#EE7B11] hover:bg-[#d06a0a]"
              >
                1
              </Button>
            ) : (
              Array.from(
                { length: Math.max(1, Math.min(pages, 5)) },
                (_, i) => {
                  let pageNumber: number;

                  // Logic to show pages around current page
                  if (pages <= 5) {
                    pageNumber = i + 1;
                  } else if (page <= 3) {
                    pageNumber = i + 1;
                  } else if (page >= pages - 2) {
                    pageNumber = pages - 4 + i;
                  } else {
                    pageNumber = page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNumber}
                      variant={page === pageNumber ? "default" : "outline"}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onPageChange(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  );
                }
              )
            )}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

type FilterType = "query" | "tags";

interface FilterItem {
  type: FilterType;
  value: string | string[];
}

const FilterContent = ({
  addFilter,
  availableTags,
  onClose,
}: {
  addFilter: (type: FilterType, value: string | string[]) => void;
  availableTags: TagsOutput[];
  onClose?: () => void;
}) => (
  <div className="space-y-4">
    <div>
      <h4 className="font-medium mb-2">Nome do Produto</h4>
      <div className="flex space-x-2">
        <Input
          placeholder="Digite o nome"
          className="h-8"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const value = (e.target as HTMLInputElement).value;
              if (value) {
                addFilter("query", value);
                onClose?.();
                (e.target as HTMLInputElement).value = "";
              }
            }
          }}
        />
      </div>
    </div>

    <div>
      <h4 className="font-medium mb-2">Tags</h4>
      <Select
        onValueChange={(value) => {
          if (value) {
            addFilter("tags", [value]);
            onClose?.();
          }
        }}
      >
        <SelectTrigger className="h-8">
          <SelectValue placeholder="Selecione as tags" />
        </SelectTrigger>
        <SelectContent>
          {availableTags.map((tag) => (
            <SelectItem key={tag.id} value={tag.id}>
              {tag.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

const ProductCard = ({
  product,
  onEdit,
  onDelete,
}: {
  product: ProductsOutput;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="p-3">
    <Card className="overflow-hidden">
      <div className="aspect-square relative bg-gray-100 flex items-center justify-center">
        {product.file?.url ? (
          <img
            src={product.file.url || "/placeholder.svg"}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <ImageIcon className="w-12 h-12 text-muted-foreground" />
        )}
      </div>
      <CardContent className="p-2">
        <h3 className="font-medium text-xs truncate">{product.name}</h3>
        <div className="flex justify-between items-center mt-1">
          <div>
            <p className="text-xs text-gray-500">
              {numberToMoney(product.unitPrice)}
            </p>
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
              onClick={onEdit}
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 border-red-500 text-red-500 hover:bg-red-50"
              onClick={onDelete}
            >
              <Trash className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {(product.tags as TagsOutput[]).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {(product.tags as TagsOutput[]).map((tag) => (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.styling?.primaryColor ?? "#E0E0E0",
                  color: tag.styling?.fontColor ?? "#000000",
                  border: `1px solid ${
                    tag.styling?.secondaryColor ?? "#BDBDBD"
                  }`,
                }}
                className="rounded-sm px-1 py-0.5 text-xs"
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

const Products = () => {
  const [products, setProducts] = useState<ProductsOutput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [availableTags, setAvailableTags] = useState<TagsOutput[]>([]);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 0,
    links: {
      self: "",
    },
  });

  const navigate = useNavigate();
  // Remover searchParams
  const productService = useMemo(() => new ProductsService(), []);

  // Referência para controlar chamadas duplicadas
  const isFetchingRef = useRef(false);

  // Hook para gerenciar a confirmação de exclusão
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  // Remover os useEffect que lidam com URL params

  const fetchProducts = useCallback(async () => {
    // Evitar chamadas duplicadas
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const queryParams: Record<string, string | string[] | number> = {
        expand: ["tags", "file"],
        page: pagination.page,
        pageSize: pagination.pageSize,
      };

      filters.forEach((filter) => {
        if (filter.type === "tags") {
          queryParams.tags = filter.value as string[];
        } else {
          queryParams[filter.type] = filter.value as string;
        }
      });

      const response: PaginatedResponse<ProductsOutput> =
        await productService.findAll(queryParams);

      // Usar o novo formato de resposta paginada
      setProducts(response.data);

      // Verificar se a página atual é válida
      if (
        response.pagination.pages > 0 &&
        pagination.page > response.pagination.pages
      ) {
        // Se a página atual for maior que o número total de páginas, redirecionar para a última página
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.pages,
        }));
      } else {
        setPagination(response.pagination);
      }

      const tags = Array.from(
        new Set(
          response.data.flatMap((product) => product.tags as TagsOutput[])
        )
      );
      setAvailableTags(tags);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        pages: 0,
      }));

      // Se ocorrer um erro, voltar para a página 1
      if (pagination.page !== 1) {
        setPagination((prev) => ({
          ...prev,
          page: 1,
        }));
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [filters, pagination.page, pagination.pageSize, productService]);

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Atualizar quando os filtros ou paginação mudarem
  useEffect(() => {
    fetchProducts();
  }, [filters, pagination.page, pagination.pageSize, fetchProducts]);

  const handleNavigateToForm = (productId?: string) => {
    const targetRoute = productId
      ? `/produtos/${productId}/editar`
      : "/produtos/novo";
    navigate(targetRoute);
  };

  const handleDeleteClick = (productId: string) => {
    openDeleteDialog(productId);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setIsLoading(true);
      await productService.delete(itemToDelete);
      await fetchProducts();
    } finally {
      setIsLoading(false);
      closeDeleteDialog();
    }
  };

  const addFilter = (type: FilterType, value: string | string[]) => {
    const existingFilterIndex = filters.findIndex((f) => f.type === type);

    if (existingFilterIndex >= 0) {
      const newFilters = [...filters];
      if (type === "tags") {
        const currentValues = newFilters[existingFilterIndex].value as string[];
        const newValue = value as string[];
        newFilters[existingFilterIndex].value = Array.from(
          new Set([...currentValues, ...newValue])
        );
      } else {
        newFilters[existingFilterIndex].value = value;
      }
      setFilters(newFilters);
    } else {
      setFilters((prev) => [
        ...prev,
        { type, value: type === "tags" ? [value as string] : value },
      ]);
    }

    // Resetar para a primeira página ao aplicar filtros
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const removeTagValue = (
    filter: FilterItem,
    valueToRemove: string
  ): FilterItem => {
    if (filter.type !== "tags") {
      return filter;
    }

    const values = filter.value as string[];
    const filteredValues = values.filter((v) => v !== valueToRemove);

    return {
      ...filter,
      value: filteredValues,
    };
  };

  const removeEmptyTagsFilters = (filter: FilterItem): boolean => {
    return filter.type !== "tags" || (filter.value as string[]).length > 0;
  };

  const removeFilter = (type: FilterType, valueToRemove?: string) => {
    setFilters((prev) => {
      if (type === "tags" && valueToRemove) {
        return prev
          .map((filter) => removeTagValue(filter, valueToRemove))
          .filter(removeEmptyTagsFilters);
      } else {
        return prev.filter((f) => f.type !== type);
      }
    });

    // Resetar para a primeira página ao remover filtros
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1, // Reset to first page when changing page size
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const FilterButton = () => (
    <>
      <div className="hidden md:block">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <FilterContent
              addFilter={addFilter}
              availableTags={availableTags}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="md:hidden">
        <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle>Filtros</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <FilterContent
                addFilter={addFilter}
                availableTags={availableTags}
                onClose={() => setIsFilterSheetOpen(false)}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );

  return (
    <div className="p-4 sm:p-6 w-full mx-auto">
      <Card className="overflow-hidden border-none shadow-md w-full">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
          <div className="flex items-center gap-3">
            <Tag className="h-6 w-6" />
            <CardTitle className="text-xl">Gerenciamento de Produtos</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <FilterButton />
              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => {
                  if (filter.type === "tags") {
                    const tagValues = filter.value as string[];
                    return tagValues.map((tagId) => {
                      const tag = availableTags.find((t) => t.id === tagId);
                      return (
                        <Badge key={tagId} variant="secondary" className="h-8">
                          Tag: {tag?.name}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-2 h-4 w-4 p-0"
                            onClick={() => removeFilter("tags", tagId)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      );
                    });
                  }
                  return (
                    <Badge
                      key={filter.type}
                      variant="secondary"
                      className="h-8"
                    >
                      {filter.type === "query"
                        ? `Nome: ${filter.value}`
                        : String(filter.value)}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-4 w-4 p-0"
                        onClick={() => removeFilter(filter.type)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 w-full md:w-auto justify-between md:justify-end">
              <div className="flex gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="sm"
                  className={
                    viewMode === "grid" ? "bg-[#EE7B11] hover:bg-[#d06a0a]" : ""
                  }
                  onClick={() => setViewMode("grid")}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="sm"
                  className={
                    viewMode === "list" ? "bg-[#EE7B11] hover:bg-[#d06a0a]" : ""
                  }
                  onClick={() => setViewMode("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button
                onClick={() => handleNavigateToForm()}
                size="sm"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> Cadastrar Produto
              </Button>
            </div>
          </div>

          {products.length > 0 ? (
            <>
              {viewMode === "grid" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onEdit={() => handleNavigateToForm(product.id)}
                      onDelete={() => handleDeleteClick(product.id)}
                    />
                  ))}
                </div>
              )}

              {viewMode === "list" && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-gray-50">
                        <TableRow>
                          <TableHead className="w-[80px]">Imagem</TableHead>
                          <TableHead>Nome do Produto</TableHead>
                          <TableHead>Preço Unitário</TableHead>
                          <TableHead>Custo Unitário</TableHead>
                          <TableHead className="text-right">Margem</TableHead>
                          <TableHead>Tags</TableHead>
                          <TableHead>Criado em</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((product) => {
                          const unitPrice = product.unitPrice || 0;
                          const unitCost = product.unitCost || 0;
                          const margin = unitPrice - unitCost;
                          const marginPercentage =
                            unitPrice > 0 ? (margin / unitPrice) * 100 : 0;

                          return (
                            <TableRow
                              key={product.id}
                              className="hover:bg-gray-50"
                            >
                              <TableCell>
                                <div className="w-12 h-12 relative flex items-center justify-center bg-gray-50 rounded-md">
                                  {product.file?.url ? (
                                    <img
                                      src={
                                        product.file.url || "/placeholder.svg"
                                      }
                                      alt={product.name}
                                      className="w-full h-full object-cover rounded-md"
                                    />
                                  ) : (
                                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">
                                {product.name}
                              </TableCell>
                              <TableCell>
                                {numberToMoney(product.unitPrice)}
                              </TableCell>
                              <TableCell>
                                {numberToMoney(product.unitCost)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex flex-col items-end gap-1">
                                  <span
                                    className={`font-medium text-sm ${
                                      margin >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {marginPercentage.toFixed(1)}%
                                  </span>
                                  <span
                                    className={`text-xs ${
                                      margin >= 0
                                        ? "text-green-500"
                                        : "text-red-500"
                                    }`}
                                  >
                                    {numberToMoney(margin)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {(product.tags as TagsOutput[]).map((tag) => (
                                    <Badge
                                      key={tag.id}
                                      style={{
                                        backgroundColor:
                                          tag.styling?.primaryColor ??
                                          "#E0E0E0",
                                        color:
                                          tag.styling?.fontColor ?? "#000000",
                                        border: `1px solid ${
                                          tag.styling?.secondaryColor ??
                                          "#BDBDBD"
                                        }`,
                                      }}
                                      className="rounded-sm px-1 py-0.5 text-xs"
                                    >
                                      {tag.name}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                {format(
                                  new Date(product.createdAt),
                                  "dd/MM/yyyy"
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                                    onClick={() =>
                                      handleNavigateToForm(product.id)
                                    }
                                  >
                                    <Edit className="h-4 w-4 mr-1" />
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-500 text-red-500 hover:bg-red-50"
                                    onClick={() =>
                                      handleDeleteClick(product.id)
                                    }
                                  >
                                    <Trash className="h-4 w-4 mr-1" />
                                    Excluir
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Adicionar o componente de paginação após o conteúdo principal */}
              <PaginationControls
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          ) : (
            <div className="text-center py-12 border rounded-lg bg-gray-50 w-full">
              <Tag className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Nenhum produto encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                {filters.length > 0
                  ? "Não encontramos produtos com os filtros aplicados"
                  : "Você ainda não possui produtos cadastrados"}
              </p>
              <Button
                onClick={() => handleNavigateToForm()}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Produto
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de confirmação de exclusão */}
      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita."
        itemName={
          products.find((p) => p.id === itemToDelete)?.name || "este produto"
        }
      />
    </div>
  );
};

export default Products;
